import * as React from 'react';

import {
  useDashboardViewStore,
  useDashboardViewOverrides,
} from 'src/components/analytics-genesis/byod/store/dashboardViewStore';
import type {WidgetConfig} from 'src/types/byod';


export type UseByodWidgetOverridesReturn = {
  /** Hydrated widgets with the viewer's local per-tile config overrides applied. */
  widgets: Record<string, WidgetConfig>;
  /**
   * Merge a session-only `widget_config` patch for one tile (e.g.
   * `selected_columns`). Written to the keyed `dashboardViewStore`, never to the
   * backend — this is the embed's local "edit table columns" persistence.
   */
  onUpdateWidget: (tileId: string, patch: Record<string, unknown>) => void;
};

/**
 * Applies the viewer's local, session-only per-tile config overrides over the
 * hydrated widgets, and exposes a writer that stores new picks in the keyed
 * `dashboardViewStore`. Keeping the overrides in the store (not tile-local
 * state) scopes them to the dashboard, so a column show/hide survives tile
 * remounts / scroll-out / tab switches within the session.
 */
export function useByodWidgetOverrides(
  dashboardId: string,
  widgets: Record<string, WidgetConfig> | undefined,
): UseByodWidgetOverridesReturn {
  const overrides = useDashboardViewOverrides(dashboardId);
  const setWidgetOverride = useDashboardViewStore((s) => s.setWidgetOverride);

  const onUpdateWidget = React.useCallback(
    (tileId: string, patch: Record<string, unknown>) =>
      setWidgetOverride(dashboardId, tileId, patch),
    [setWidgetOverride, dashboardId],
  );

  // Layer overrides over the hydrated widgets. Returns the SAME reference when
  // there are no overrides so the canvas doesn't needlessly re-render every tile.
  const widgetsWithOverrides = React.useMemo(() => {
    if (!widgets) {
      return {};
    }
    const ids = Object.keys(overrides);
    if (ids.length === 0) {
      return widgets;
    }
    const next: Record<string, WidgetConfig> = {...widgets};
    for (const id of ids) {
      const w = next[id];
      if (!w) {
        continue;
      }
      next[id] = {
        ...w,
        widget_config_json: {...w.widget_config_json, ...overrides[id]},
      };
    }
    return next;
  }, [widgets, overrides]);

  return {widgets: widgetsWithOverrides, onUpdateWidget};
}
